import { ChatMessages } from "@storytell/cp-client";
import {
  getActivityColorClass,
  getActivityConfig,
  getActivityIconKey,
  isEndTurnOperationId,
  type StreamingChatId,
  StreamingChatStatus,
  type StreamingOperation,
} from "@storytell/streaming";
import {
  TbAddressBook,
  TbAlertCircle,
  TbBooks,
  TbBrain,
  TbBrandGithub,
  TbBrandGmail,
  TbBrandGoogleDrive,
  TbBrandNotion,
  TbBrandSlack,
  TbBulb,
  TbCalendar,
  TbChartBar,
  TbCheck,
  TbChevronDown,
  TbClipboardList,
  TbDatabase,
  TbFileText,
  TbGlobe,
  TbHourglass,
  TbMessage,
  TbMessageCircle,
  TbMessageQuestion,
  TbPhoto,
  TbPlug,
  TbPresentation,
  TbRefresh,
  TbRobot,
  TbRoute,
  TbSparkles,
  TbUser,
  TbVideo,
  TbWand,
  TbWaveSquare,
} from "solid-icons/tb";
import { type Component, type JSX, Match, Show, Switch } from "solid-js";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { LinearIcon } from "../tools/linear/linearIcon";
import { ProcessingSpinner } from "../ui/ProcessingSpinner";
import { collapsibleOpenHint } from "../util/collapsibleOpenHint";

// Inlined from the bluesky LogoNoText asset so the shared package needs no
// cross-app import.
const ChiefMark: Component<{ class?: string }> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25" fill="none" class={props.class}>
    <title>Chief</title>
    <rect y="0.869141" width="23.0473" height="23.3153" rx="11.5237" fill="#43416B" />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M6.81443 5.5429C6.81443 5.46096 6.74842 5.39453 6.66698 5.39453C6.58555 5.39453 6.51953 5.46096 6.51953 5.5429C6.51953 5.62484 6.58555 5.69126 6.66698 5.69126C6.74842 5.69126 6.81443 5.62484 6.81443 5.5429ZM6.66698 7.15019C6.74842 7.15019 6.81443 7.21661 6.81443 7.29855C6.81443 7.38049 6.74842 7.44692 6.66698 7.44692C6.58555 7.44692 6.51953 7.38049 6.51953 7.29855C6.51953 7.21661 6.58555 7.15019 6.66698 7.15019ZM6.81443 9.05421C6.81443 8.97227 6.74842 8.90584 6.66698 8.90584C6.58555 8.90584 6.51953 8.97227 6.51953 9.05421C6.51953 9.13615 6.58555 9.20257 6.66698 9.20257C6.74842 9.20257 6.81443 9.13615 6.81443 9.05421ZM6.66698 10.6615C6.74842 10.6615 6.81443 10.7279 6.81443 10.8099C6.81443 10.8918 6.74842 10.9582 6.66698 10.9582C6.58555 10.9582 6.51953 10.8918 6.51953 10.8099C6.51953 10.7279 6.58555 10.6615 6.66698 10.6615ZM6.81443 12.5655C6.81443 12.4836 6.74842 12.4172 6.66698 12.4172C6.58555 12.4172 6.51953 12.4836 6.51953 12.5655C6.51953 12.6475 6.58555 12.7139 6.66698 12.7139C6.74842 12.7139 6.81443 12.6475 6.81443 12.5655ZM6.66698 14.1728C6.74842 14.1728 6.81443 14.2392 6.81443 14.3212C6.81443 14.4031 6.74842 14.4695 6.66698 14.4695C6.58555 14.4695 6.51953 14.4031 6.51953 14.3212C6.51953 14.2392 6.58555 14.1728 6.66698 14.1728ZM6.81443 16.0768C6.81443 15.9949 6.74842 15.9285 6.66698 15.9285C6.58555 15.9285 6.51953 15.9949 6.51953 16.0768C6.51953 16.1588 6.58555 16.2252 6.66698 16.2252C6.74842 16.2252 6.81443 16.1588 6.81443 16.0768ZM6.66698 17.6841C6.74842 17.6841 6.81443 17.7505 6.81443 17.8325C6.81443 17.9144 6.74842 17.9809 6.66698 17.9809C6.58555 17.9809 6.51953 17.9144 6.51953 17.8325C6.51953 17.7505 6.58555 17.6841 6.66698 17.6841ZM6.66698 19.4398C6.74842 19.4398 6.81443 19.5062 6.81443 19.5881C6.81443 19.6701 6.74842 19.7365 6.66698 19.7365C6.58555 19.7365 6.51953 19.6701 6.51953 19.5881C6.51953 19.5062 6.58555 19.4398 6.66698 19.4398ZM7.25679 18.6238C7.35179 18.6238 7.42881 18.7013 7.42881 18.7969C7.42881 18.8925 7.35179 18.97 7.25679 18.97C7.16178 18.97 7.08476 18.8925 7.08476 18.7969C7.08476 18.7013 7.16178 18.6238 7.25679 18.6238ZM7.42881 17.0165C7.42881 16.9209 7.35179 16.8434 7.25679 16.8434C7.16178 16.8434 7.08476 16.9209 7.08476 17.0165C7.08476 17.1121 7.16178 17.1896 7.25679 17.1896C7.35179 17.1896 7.42881 17.1121 7.42881 17.0165ZM7.25679 15.063C7.35179 15.063 7.42881 15.1405 7.42881 15.2361C7.42881 15.3317 7.35179 15.4092 7.25679 15.4092C7.16178 15.4092 7.08476 15.3317 7.08476 15.2361C7.08476 15.1405 7.16178 15.063 7.25679 15.063ZM7.42881 13.4557C7.42881 13.3601 7.35179 13.2826 7.25679 13.2826C7.16178 13.2826 7.08476 13.3601 7.08476 13.4557C7.08476 13.5513 7.16178 13.6288 7.25679 13.6288C7.35179 13.6288 7.42881 13.5513 7.42881 13.4557ZM7.25679 11.5022C7.35179 11.5022 7.42881 11.5797 7.42881 11.6753C7.42881 11.7709 7.35179 11.8484 7.25679 11.8484C7.16178 11.8484 7.08476 11.7709 7.08476 11.6753C7.08476 11.5797 7.16178 11.5022 7.25679 11.5022ZM7.42881 9.89494C7.42881 9.79935 7.35179 9.72185 7.25679 9.72185C7.16178 9.72185 7.08476 9.79935 7.08476 9.89494C7.08476 9.99054 7.16178 10.068 7.25679 10.068C7.35179 10.068 7.42881 9.99054 7.42881 9.89494ZM7.25679 6.16109C7.35179 6.16109 7.42881 6.23858 7.42881 6.33418C7.42881 6.42977 7.35179 6.50727 7.25679 6.50727C7.16178 6.50727 7.08476 6.42977 7.08476 6.33418C7.08476 6.23858 7.16178 6.16109 7.25679 6.16109ZM7.42881 8.11456C7.42881 8.01897 7.35179 7.94147 7.25679 7.94147C7.16178 7.94147 7.08476 8.01897 7.08476 8.11456C7.08476 8.21016 7.16178 8.28765 7.25679 8.28765C7.35179 8.28765 7.42881 8.21016 7.42881 8.11456ZM7.89574 17.6347C8.01789 17.6347 8.11692 17.7343 8.11692 17.8572C8.11692 17.9801 8.01789 18.0798 7.89574 18.0798C7.77359 18.0798 7.67456 17.9801 7.67456 17.8572C7.67456 17.7343 7.77359 17.6347 7.89574 17.6347ZM8.11692 16.1016C8.11692 15.9787 8.01789 15.879 7.89574 15.879C7.77359 15.879 7.67456 15.9787 7.67456 16.1016C7.67456 16.2245 7.77359 16.3241 7.89574 16.3241C8.01789 16.3241 8.11692 16.2245 8.11692 16.1016ZM7.89574 14.0986C8.01789 14.0986 8.11692 14.1983 8.11692 14.3212C8.11692 14.4441 8.01789 14.5437 7.89574 14.5437C7.77359 14.5437 7.67456 14.4441 7.67456 14.3212C7.67456 14.1983 7.77359 14.0986 7.89574 14.0986ZM8.11692 12.5408C8.11692 12.4179 8.01789 12.3182 7.89574 12.3182C7.77359 12.3182 7.67456 12.4179 7.67456 12.5408C7.67456 12.6637 7.77359 12.7633 7.89574 12.7633C8.01789 12.7633 8.11692 12.6637 8.11692 12.5408ZM7.89574 10.5626C8.01789 10.5626 8.11692 10.6622 8.11692 10.7851C8.11692 10.908 8.01789 11.0077 7.89574 11.0077C7.77359 11.0077 7.67456 10.908 7.67456 10.7851C7.67456 10.6622 7.77359 10.5626 7.89574 10.5626ZM8.11692 9.02948C8.11692 8.90657 8.01789 8.80693 7.89574 8.80693C7.77359 8.80693 7.67456 8.90657 7.67456 9.02948C7.67456 9.15239 7.77359 9.25203 7.89574 9.25203C8.01789 9.25203 8.11692 9.15239 8.11692 9.02948ZM7.89574 7.02655C8.01789 7.02655 8.11692 7.12619 8.11692 7.2491C8.11692 7.37201 8.01789 7.47165 7.89574 7.47165C7.77359 7.47165 7.67456 7.37201 7.67456 7.2491C7.67456 7.12619 7.77359 7.02655 7.89574 7.02655ZM9.09992 16.967C9.09992 16.8031 8.96789 16.6703 8.80502 16.6703C8.64215 16.6703 8.51012 16.8031 8.51012 16.967C8.51012 17.1309 8.64215 17.2638 8.80502 17.2638C8.96789 17.2638 9.09992 17.1309 9.09992 16.967ZM8.80502 14.9146C8.96789 14.9146 9.09992 15.0475 9.09992 15.2114C9.09992 15.3752 8.96789 15.5081 8.80502 15.5081C8.64215 15.5081 8.51012 15.3752 8.51012 15.2114C8.51012 15.0475 8.64215 14.9146 8.80502 14.9146ZM9.09992 13.4557C9.09992 13.2918 8.96789 13.159 8.80502 13.159C8.64215 13.159 8.51012 13.2918 8.51012 13.4557C8.51012 13.6196 8.64215 13.7524 8.80502 13.7524C8.96789 13.7524 9.09992 13.6196 9.09992 13.4557ZM8.80502 11.3786C8.96789 11.3786 9.09992 11.5114 9.09992 11.6753C9.09992 11.8392 8.96789 11.9721 8.80502 11.9721C8.64215 11.9721 8.51012 11.8392 8.51012 11.6753C8.51012 11.5114 8.64215 11.3786 8.80502 11.3786ZM9.09992 9.89494C9.09992 9.73107 8.96789 9.59821 8.80502 9.59821C8.64215 9.59821 8.51012 9.73107 8.51012 9.89494C8.51012 10.0588 8.64215 10.1917 8.80502 10.1917C8.96789 10.1917 9.09992 10.0588 9.09992 9.89494ZM8.80502 7.84256C8.96789 7.84256 9.09992 7.97541 9.09992 8.13929C9.09992 8.30317 8.96789 8.43602 8.80502 8.43602C8.64215 8.43602 8.51012 8.30317 8.51012 8.13929C8.51012 7.97541 8.64215 7.84256 8.80502 7.84256ZM10.3287 12.5284C10.3287 12.3304 10.1691 12.1699 9.97234 12.1699C9.77554 12.1699 9.616 12.3304 9.616 12.5284C9.616 12.7264 9.77554 12.887 9.97234 12.887C10.1691 12.887 10.3287 12.7264 10.3287 12.5284ZM9.97234 10.439C10.1691 10.439 10.3287 10.5995 10.3287 10.7975C10.3287 10.9955 10.1691 11.156 9.97234 11.156C9.77554 11.156 9.616 10.9955 9.616 10.7975C9.616 10.5995 9.77554 10.439 9.97234 10.439ZM10.3287 9.01712C10.3287 8.8191 10.1691 8.65857 9.97234 8.65857C9.77554 8.65857 9.616 8.8191 9.616 9.01712C9.616 9.21514 9.77554 9.37567 9.97234 9.37567C10.1691 9.37567 10.3287 9.21514 10.3287 9.01712ZM9.97234 13.975C10.1691 13.975 10.3287 14.1355 10.3287 14.3335C10.3287 14.5316 10.1691 14.6921 9.97234 14.6921C9.77554 14.6921 9.616 14.5316 9.616 14.3335C9.616 14.1355 9.77554 13.975 9.97234 13.975ZM10.3287 16.0892C10.3287 15.8912 10.1691 15.7306 9.97234 15.7306C9.77554 15.7306 9.616 15.8912 9.616 16.0892C9.616 16.2872 9.77554 16.4477 9.97234 16.4477C10.1691 16.4477 10.3287 16.2872 10.3287 16.0892ZM11.238 14.7663C11.4823 14.7663 11.6803 14.9655 11.6803 15.2114C11.6803 15.4572 11.4823 15.6565 11.238 15.6565C10.9937 15.6565 10.7956 15.4572 10.7956 15.2114C10.7956 14.9655 10.9937 14.7663 11.238 14.7663ZM11.6803 13.431C11.6803 13.1852 11.4823 12.9859 11.238 12.9859C10.9937 12.9859 10.7956 13.1852 10.7956 13.431C10.7956 13.6768 10.9937 13.8761 11.238 13.8761C11.4823 13.8761 11.6803 13.6768 11.6803 13.431ZM11.238 11.2302C11.4823 11.2302 11.6803 11.4295 11.6803 11.6753C11.6803 11.9211 11.4823 12.1204 11.238 12.1204C10.9937 12.1204 10.7956 11.9211 10.7956 11.6753C10.7956 11.4295 10.9937 11.2302 11.238 11.2302ZM11.6803 9.89494C11.6803 9.64913 11.4823 9.44985 11.238 9.44985C10.9937 9.44985 10.7956 9.64913 10.7956 9.89494C10.7956 10.1408 10.9937 10.34 11.238 10.34C11.4823 10.34 11.6803 10.1408 11.6803 9.89494ZM12.9091 13.7524C13.2212 13.7524 13.4743 14.0071 13.4743 14.3212C13.4743 14.6353 13.2212 14.8899 12.9091 14.8899C12.5969 14.8899 12.3438 14.6353 12.3438 14.3212C12.3438 14.0071 12.5969 13.7524 12.9091 13.7524ZM13.4743 12.5408C13.4743 12.2267 13.2212 11.9721 12.9091 11.9721C12.5969 11.9721 12.3438 12.2267 12.3438 12.5408C12.3438 12.8549 12.5969 13.1095 12.9091 13.1095C13.2212 13.1095 13.4743 12.8549 13.4743 12.5408ZM12.9091 10.1917C13.2212 10.1917 13.4743 10.4463 13.4743 10.7604C13.4743 11.0745 13.2212 11.3291 12.9091 11.3291C12.5969 11.3291 12.3438 11.0745 12.3438 10.7604C12.3438 10.4463 12.5969 10.1917 12.9091 10.1917ZM15.514 13.431C15.514 13.0486 15.206 12.7386 14.8259 12.7386C14.4459 12.7386 14.1378 13.0486 14.1378 13.431C14.1378 13.8134 14.4459 14.1234 14.8259 14.1234C15.206 14.1234 15.514 13.8134 15.514 13.431ZM14.8259 10.983C15.206 10.983 15.514 11.2929 15.514 11.6753C15.514 12.0577 15.206 12.3677 14.8259 12.3677C14.4459 12.3677 14.1378 12.0577 14.1378 11.6753C14.1378 11.2929 14.4459 10.983 14.8259 10.983ZM18.119 12.5408C18.119 12.0492 17.7229 11.6506 17.2343 11.6506C16.7457 11.6506 16.3496 12.0492 16.3496 12.5408C16.3496 13.0324 16.7457 13.431 17.2343 13.431C17.7229 13.431 18.119 13.0324 18.119 12.5408Z"
      fill="url(#chief_mark_gradient)"
    />
    <defs>
      <linearGradient
        id="chief_mark_gradient"
        x1="6.66698"
        y1="12.5655"
        x2="17.222"
        y2="12.5655"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0.15625" stop-color="#FB68FF" />
        <stop offset="0.822127" stop-color="#61E0FB" />
      </linearGradient>
    </defs>
  </svg>
);

export const ACTIVITY_ICON_MAP: Record<string, Component<{ class?: string }>> = {
  check: (props) => <TbCheck class={`stroke-3 ${props.class || ""}`} />,
  brain: TbBrain,
  books: TbBooks,
  globe: TbGlobe,
  bulb: TbBulb,
  photo: TbPhoto,
  video: TbVideo,
  chart: TbChartBar,
  presentation: TbPresentation,
  "file-text": TbFileText,
  "message-question": TbMessageQuestion,
  route: TbRoute,
  robot: TbRobot,
  database: TbDatabase,
  hourglass: TbHourglass,
  "message-circle": TbMessageCircle,
  sparkles: TbSparkles,
  wand: TbWand,
  "clipboard-list": TbClipboardList,
  "address-book": TbAddressBook,
  "brand-google-drive": TbBrandGoogleDrive,
  "brand-notion": TbBrandNotion,
  "brand-slack": TbBrandSlack,
  "brand-gmail": TbBrandGmail,
  "brand-linear": LinearIcon,
  "brand-github": TbBrandGithub,
  calendar: TbCalendar,
  plug: TbPlug,
  message: TbMessage,
  "wave-square": TbWaveSquare,
  user: TbUser,
};

interface OperationHeaderProps {
  operation: StreamingOperation;
  title: string;
  context?: string;
  isExpanded: boolean;
  isProcessing: boolean;
  hasBlocks: boolean;
  onToggle: () => void;
  chatId: StreamingChatId;
  isPlanning: boolean;
  compact?: boolean;
  isSearchOperation?: boolean;
  hasNoResults?: boolean;
  emptyMessageKey?: string;
  downloadContent?: () => string;
  downloadSubtitle?: () => string | undefined;
  downloadTooltip?: string;
  copyTooltip?: string;
  addTooltip?: string;
}

export const OperationHeader: Component<OperationHeaderProps> = (props) => {
  const rt = useChatRuntime();

  const getOperationIcon = (operation: StreamingOperation) => {
    const isStreaming =
      (!operation.isComplete ||
        operation.status === ChatMessages.OperationStatus.RUNNING ||
        operation.status === ChatMessages.OperationStatus.STARTING) &&
      operation.status !== ChatMessages.OperationStatus.RETRYING;

    let iconElement: JSX.Element;
    let backgroundClass = "bg-dark-icon text-primary-text";

    if (operation.status === ChatMessages.OperationStatus.RETRYING) {
      iconElement = <TbRefresh class="size-3.5" />;
      backgroundClass = "bg-gray-500 dark:bg-gray-400 text-white";
    } else if (isStreaming) {
      iconElement = <ProcessingSpinner class="border-[1.5px] border-primary-text border-t-transparent" />;
    } else if (
      operation.status === ChatMessages.OperationStatus.FAILED ||
      operation.status === ChatMessages.OperationStatus.CANCELLED
    ) {
      iconElement = <TbAlertCircle class="size-3.5" />;
      const hasNonFatalError = (operation.blocks ?? []).some((blockId) => {
        const block = rt.store.block(blockId);
        if (block?.finalContent?.type === "error") {
          return block.finalContent.isFatal === false;
        }
        return false;
      });
      backgroundClass = hasNonFatalError
        ? "bg-gray-500 dark:bg-gray-400 text-white"
        : "bg-red-600 dark:bg-red-500 text-white";
    } else if (props.isPlanning) {
      iconElement = <TbRoute class="size-3.5" />;
      backgroundClass = "bg-indigo-500 dark:bg-indigo-400 text-white";
    } else if (operation.activity === "chief") {
      iconElement = <ChiefMark class="size-6" />;
      backgroundClass = "bg-transparent";
    } else {
      // Use registry for standard activity icons. Fall back to operation.name for
      // nested agent operations where activity is "agent_tool_...".
      const hasRegistryConfig = !!getActivityConfig(operation.activity);
      const effectiveActivity = hasRegistryConfig ? operation.activity : operation.name;
      const iconKey = getActivityIconKey(effectiveActivity);
      const registryColorClass = getActivityColorClass(effectiveActivity);
      const isKbSearch =
        operation.activity === "kb_search" ||
        operation.activity === "user_kb_search_v1" ||
        operation.activity === "knowledge_base_search";

      const IconComponent = ACTIVITY_ICON_MAP[iconKey];

      // Post-completion: if the resolved icon would be the generic check (either
      // via iconKey "check" or the unmapped-iconKey fallback), render nothing.
      // The spinner conveyed "running"; a residual checkmark is just noise.
      if (!IconComponent || iconKey === "check") return null;

      // Knowledge Search book icon: grey so it's visible on light stone background
      const iconClass = isKbSearch ? "w-3.5 h-3.5 text-gray-600 dark:text-gray-300" : "w-3.5 h-3.5";
      iconElement = <IconComponent class={iconClass} />;

      if (registryColorClass && registryColorClass !== "bg-dark-icon") {
        backgroundClass = isKbSearch ? registryColorClass : `${registryColorClass} text-white`;
      }
    }

    return (
      <div class="flex size-6 items-center justify-center rounded-full" classList={{ [backgroundClass]: true }}>
        {iconElement}
      </div>
    );
  };

  const endTurnIdle = () =>
    isEndTurnOperationId(props.operation.operationId) &&
    rt.store.chatStatus?.(props.chatId) === StreamingChatStatus.IDLE;

  return (
    <>
      <div
        data-renderer="operation-header"
        class="group/op-header flex items-center justify-between"
        classList={{
          "mb-4": !props.compact,
          "mb-2": props.compact && props.isExpanded,
        }}
        data-operation={props.operation.operationId}
      >
        <Switch>
          <Match when={!isEndTurnOperationId(props.operation.operationId) || endTurnIdle()}>
            <button
              type="button"
              onClick={() => !props.hasNoResults && props.onToggle()}
              class="flex min-w-0 flex-1 items-center"
              classList={{
                "gap-2.5": !props.compact,
                "gap-2": props.compact,
                "cursor-pointer": !props.hasNoResults,
                "cursor-default": props.hasNoResults,
              }}
            >
              <Show when={!props.hasNoResults}>
                <TbChevronDown
                  size={props.compact ? 10 : 12}
                  class="shrink-0 stroke-3 text-muted-text transition-transform duration-100"
                  classList={{
                    "-rotate-90": !props.isExpanded,
                  }}
                />
              </Show>
              <Show when={getOperationIcon(props.operation)}>{(icon) => <div class="shrink-0">{icon()}</div>}</Show>
              <div class="min-w-0 flex-1">
                <div class="group flex items-center gap-1.5">
                  <span class="min-w-0 truncate text-left text-sm font-medium text-primary-text transition-colors">
                    {props.title}
                  </span>
                  <Show when={props.context && (props.compact || props.isSearchOperation) && !props.isExpanded}>
                    <span
                      class="min-w-0 flex-1 overflow-hidden text-left text-xs whitespace-nowrap text-muted-text"
                      style={{
                        "mask-image": "linear-gradient(to right, black 92%, transparent 100%)",
                        "-webkit-mask-image": "linear-gradient(to right, black 92%, transparent 100%)",
                      }}
                    >
                      {props.context}
                    </span>
                  </Show>
                  <Show when={props.hasNoResults && props.emptyMessageKey && !props.isExpanded}>
                    <span class="ml-1 shrink-0 text-xs text-amber-600 dark:text-amber-400">
                      {rt.t(props.emptyMessageKey as string)}
                    </span>
                  </Show>
                </div>
              </div>
            </button>
          </Match>
          <Match when={isEndTurnOperationId(props.operation.operationId)}>
            <div
              class="flex min-w-0 flex-1 items-center"
              classList={{
                "gap-2.5": !props.compact,
                "gap-2": props.compact,
              }}
            >
              <Show when={getOperationIcon(props.operation)}>{(icon) => <div class="shrink-0">{icon()}</div>}</Show>
              <div class="min-w-0 flex-1">
                <div class="group flex items-center gap-1">
                  <div class="text-left text-sm font-medium text-primary-text transition-colors hover:text-secondary-text">
                    {props.operation.status === ChatMessages.OperationStatus.COMPLETED ? "Done" : "Processing..."}
                  </div>
                </div>
              </div>
            </div>
          </Match>
        </Switch>
        <Show when={props.downloadContent}>
          {(downloadContent) => (
            <Show when={props.isExpanded && !props.isProcessing && downloadContent()()}>
              {rt.slots?.operationActions?.({
                downloadContent: downloadContent(),
                downloadSubtitle: props.downloadSubtitle,
                downloadTooltip: props.downloadTooltip,
                copyTooltip: props.copyTooltip,
                addTooltip: props.addTooltip,
                title: props.title,
                context: props.context,
              })}
            </Show>
          )}
        </Show>
        <Show
          when={
            !props.isExpanded &&
            props.hasBlocks &&
            !props.hasNoResults &&
            !isEndTurnOperationId(props.operation.operationId)
          }
        >
          <span class={collapsibleOpenHint.opHeader} aria-hidden>
            {rt.t("common.open")}
          </span>
        </Show>
      </div>
    </>
  );
};
